
/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/DockerRegistryEndpoint/Components/DockerRegistryEndpointEditorComponent" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { InputValidation, InputDataType } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";
import { ServiceEndpoint, AzureSubscription, AccessTokenRequestType, ServiceEndpointRequest, ServiceEndpointRequestResult } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import * as ServiceEndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { IAzureResourcesService } from "DistributedTask/Common/AzureResourcesService/AzureResourcesService";
import { GenericEndpointsDetailsComponents } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericEndpointsDetailsComponents";
import { GenericFooterComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericFooterComponent";
import { EndpointsEditorPanelUtils } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { SubscriptionPicker, SubscriptionConsumer, ISubscriptionContextValue, Subscription, SubscriptionProvider } from "DistributedTask/Subscriptions/SubscriptionPicker";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { EndpointsEditorStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { ActionsCreatorInstanceIds, TestConnectionDataSource, ConfidentialInputPlaceHolder, FeatureAvailabilityFlags } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { StoreManager } from "DistributedTask/Flux/StoreManager";

import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { RadioButtonGroupDirection } from "VSSUI/Components/RadioButton/RadioButton.Props";
import { Observer } from "VSSUI/Observer";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ObservableValue } from "VSS/Core/Observable";
import * as Utils_String from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageService } from "VSS/Platform/Page";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";

export interface IDockerRegistryComponentProps extends IEndpointEditorComponentProps {
    hidePanelFooter: (hideFooter: boolean) => void;
    closePanel: (serviceEndpoint: ServiceEndpoint) => void;
    onBackButtonClick: () => void;
    hideBackButton: boolean;
    isVerifiableConnection: boolean;
    updateError: (isError: boolean, errorMessage: string, errorStatus?: string) => void;
    errorMessage: string;
}

export interface IDockerRegistryComponentState {
    selectedSubscription: AzureSubscription;
    accessTokenKey: string;
    fetchingMoreACRs: boolean;
    acrList: any[];
    selectedAcr: any;
    verifyInProgress: boolean;
    saveInProgress: boolean;
    setupInProgress: boolean;
    isError: boolean;
    errorMessage: string;
    isVerifyInProgress?: boolean;
    isVerificationSuccess?: boolean;
}

export class DockerRegistryEndpointEditorComponent extends VssComponent<IDockerRegistryComponentProps, IDockerRegistryComponentState> {

    constructor(props: IDockerRegistryComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._endpointEditorActionCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>(EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);
        this._endpointEditorStore = StoreManager.GetStore<EndpointsEditorStore>(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);
        this._endpointEditorStore.addChangedListener(this._onEndpointEditorStoreChange);
        this._azureEndpointService = context.pageContext.getService<IAzureResourcesService>("IAzureResourcesService");
        this._initializeValues();
        this.props.hidePanelFooter(true);
        this.setTimeout(() => {
            this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        });
    }

    public componentWillUnmount() {
        this._endpointEditorStore.removeChangedListener(this._onEndpointEditorStoreChange);
    }

    private _initializeValues() {
        this._draftServiceEndpoint = this.props.draftEndpoint;
        this._draftServiceEndpoint.data = this._draftServiceEndpoint.data || {};
        this.state = {
            selectedSubscription: this._getSubscriptionFromEndpoint(),
            accessTokenKey: undefined,
            fetchingMoreACRs: false,
            acrList: [],
            selectedAcr: null,
            verifyInProgress: false,
            saveInProgress: false,
            setupInProgress: false,
            isError: false,
            errorMessage: ""
        };

        if (!this.props.isUpdateForm) {
            this._draftServiceEndpoint.url = this._draftServiceEndpoint.data.registrytype === this._dockerRegistryTypeDockerHub ?
                this._dockerHubDefaultUrl : this._dockerRegistryDefaultUrl;
            this._draftServiceEndpoint.authorization.parameters.registry = this._dockerRegistryDefaultUrl;
        }

        this._isSaveEnabled = new ObservableValue(false);

        if (this.props.isUpdateForm) {
            this._acrRegistryTypeAuthParamsCache = this._draftServiceEndpoint.authorization.parameters;
            this._acrRegistryTypeDataParamsCache = this._draftServiceEndpoint.data;
        }

        this._invalidComponentCount = 0;
        this._initializeInputErrors();

        this._authenticationTypeOptions.push({
            id: ServiceEndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal,
            text: Resources.AuthenticationSPN,
            data: ServiceEndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal
        });

        if (isFeatureFlagEnabled(this.context.pageContext, FeatureAvailabilityFlags.EnableMSIForACR, false)) {
            this._authenticationTypeOptions.push({
                id: ServiceEndpointConstants.EndpointAuthorizationSchemes.ManagedServiceIdentity,
                text: Resources.AuthenticationMSI,
                data: ServiceEndpointConstants.EndpointAuthorizationSchemes.ManagedServiceIdentity
            });
        }
        else {
            // default auth type when service principal is the only option
            if (!this.props.isUpdateForm) {
                this._onACRAuthenticationTypeSelected(ServiceEndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal);
            }
        }
    }

    public render(): React.ReactNode {
        return (
            <React.Fragment>
                <div>
                    {this._getRegistryTypePicker()}
                    {this._draftServiceEndpoint.data.registrytype === this._dockerRegistryTypeACR && this._getACRAuthenticationTypeComponent()}
                    {this._getAuthenticationComponent()}
                    {/* This verificationcomponent is being rendered in other endpoint components as well. Ensure to make similar changes at other places if this is updated */}
                    {this._getVerificationComponent()}
                    {this._getDetailsComponent()}
                </div>
                { this.state.isError && <MessageCard
                    className="flex-self-stretch margin-top-16 margin-bottom-16"
                    severity={MessageCardSeverity.Error}
                   >
                   {this.state.errorMessage}
                </MessageCard>}
                {this._getFooterComponent()}
            </React.Fragment>
        );
    }

    private _getRegistryTypePicker(): React.ReactNode {
        return (
            <RadioButtonGroup
                text={Resources.Registrytype}
                direction={RadioButtonGroupDirection.Horizontal}
                onSelect={(registryType: string) => {
                    this._setDefaultValuesForRegistryType(registryType);
                    this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                }}
                selectedButtonId={this._draftServiceEndpoint.data.registrytype || this._dockerRegistryTypeOthers}
            >
                <RadioButton key={"key_1"} id={this._dockerRegistryTypeDockerHub} text={Resources.DockerHubRegistryType} />
                <RadioButton key={"key_2"} id={this._dockerRegistryTypeOthers} text={Resources.OthersRegistryType} />
                {this._showOAuthScheme() && <RadioButton key={"key_3"} id={this._dockerRegistryTypeACR} text={Resources.ACRRegistryType} />}
            </RadioButtonGroup>
        );
    }

    private _getACRAuthenticationTypeComponent(): JSX.Element {

        return (
            <div>
                {<FormItem
                    className="default-input-margin-top"
                    label={Resources.Authentication}>
                    <Dropdown
                        disabled={false}
                        placeholder={this._getACRAuthenticationTypeComponentPlaceholder()}
                        items={this._authenticationTypeOptions}
                        onSelect={(event, item) => { this._onACRAuthenticationTypeSelected(item.id); }}
                    />
                </FormItem>
                }
            </div>
        );
    }

    private _getACRAuthenticationTypeComponentPlaceholder(): string {
        const scheme = this._draftServiceEndpoint.authorization.scheme;
        if (!scheme)
        {
            return "";
        }

        const authenticationTypeOption = this._authenticationTypeOptions.find(item => item.id.toUpperCase() === scheme.toUpperCase());
        if (!authenticationTypeOption)
        {
            return "";
        }

        return authenticationTypeOption.text;
    }

    private _getAuthenticationComponent(): JSX.Element {
        let isEditable = true;

        if (this._draftServiceEndpoint.data.registrytype === this._dockerRegistryTypeACR) {
            if (this._draftServiceEndpoint.authorization.scheme == null || this._draftServiceEndpoint.authorization.scheme === "" ) {
                return null;
            }

            return (
                <div>
                    {this.props.isUpdateForm && this._draftServiceEndpoint.authorization.scheme === ServiceEndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal &&
                        <FormItem
                            className="default-input-margin-top"
                            label={Resources.Subscription}>
                            <Dropdown
                                disabled={true}
                                placeholder={this.state.selectedSubscription && this.state.selectedSubscription.subscriptionId || ""}
                                items={[]}
                            />
                        </FormItem>
                    }
                    {!this.props.isUpdateForm && this._draftServiceEndpoint.authorization.scheme === ServiceEndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal &&
                        <SubscriptionProvider pageContext={this.context.pageContext}>
                            <SubscriptionConsumer>
                                {(subscriptionContext: ISubscriptionContextValue) => {
                                    if (subscriptionContext && subscriptionContext.state && subscriptionContext.state.subscriptions) {
                                        const isLoadComplete = subscriptionContext.state && subscriptionContext.state.loadedAllSubscriptions;
                                        const subscriptions: Subscription[] | undefined = isLoadComplete ? subscriptionContext.state.subscriptions : undefined;
                                        if (!this.state.selectedSubscription && subscriptions && subscriptions[0]) {
                                            setTimeout(() => {
                                                this._onSubscriptionChanged(subscriptions[0]);
                                            });
                                        }

                                        return (
                                            <SubscriptionPicker
                                                useDropdown={true}
                                                label={Resources.Subscription}
                                                showCreateSubscriptionButton={false}
                                                subscriptionContext={subscriptionContext}
                                                onSubscriptionChoiceChanged={this._onSubscriptionChanged}
                                                selectedSubscriptionId={this.state.selectedSubscription && this.state.selectedSubscription.subscriptionId || ""}
                                                className="default-input-margin-bottom default-input-margin-top"
                                            />);
                                    } else {
                                        return (subscriptionContext.state.loadedAllSubscriptions ? Resources.NoSubscriptionFound :
                                            <Spinner
                                                ariaLive="assertive"
                                                label={Resources.LoadingSubscriptions}
                                                size={SpinnerSize.medium}
                                                className="default-input-margin-top"
                                            />);
                                    }
                                }}
                            </SubscriptionConsumer>
                        </SubscriptionProvider>
                    }

                    {this._draftServiceEndpoint.authorization.scheme === ServiceEndpointConstants.EndpointAuthorizationSchemes.ManagedServiceIdentity && this._createTextBox(InputDataType.Guid, "subscription-id",
                        this._draftServiceEndpoint.data.subscriptionId , "", Resources.SubscriptionId, false, false, -1, isEditable, false)}
                    {this._draftServiceEndpoint.authorization.scheme === ServiceEndpointConstants.EndpointAuthorizationSchemes.ManagedServiceIdentity && this._createTextBox(InputDataType.String, "subscription-name",
                        this._draftServiceEndpoint.data.subscriptionName, "", Resources.SubscriptionName, false, false, -1, isEditable, false)}
                    {this._draftServiceEndpoint.authorization.scheme === ServiceEndpointConstants.EndpointAuthorizationSchemes.ManagedServiceIdentity && this._createTextBox(InputDataType.Guid, "tenant-id",
                        this._draftServiceEndpoint.authorization.parameters.tenantId, "", Resources.tenantId, false, false, -1, isEditable, false)}
                    {this._draftServiceEndpoint.authorization.scheme === ServiceEndpointConstants.EndpointAuthorizationSchemes.ManagedServiceIdentity && this._createTextBox(InputDataType.String, "acr-login-server",
                        this._draftServiceEndpoint.authorization.parameters.loginServer, "", Resources.ACRLoginServer, false, false, -1, isEditable, false)}

                    {this._draftServiceEndpoint.authorization.scheme === ServiceEndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal &&
                        (this.state.selectedSubscription || this.props.isUpdateForm) &&
                        (this.state.acrList.length || this.props.isUpdateForm ?
                        <React.Fragment>
                            <div className="default-input-margin-top">
                                {Resources.ACRPickerLabel}
                            </div>
                            <Dropdown
                                ariaLabel={Resources.ACRPickerLabel}
                                disabled={this.props.isUpdateForm}
                                placeholder={this._getRegistryIdFromEndpoint()}
                                selection={this._getACRSelection()}
                                items={this.state.acrList.map((acr) => {
                                    return {
                                        id: acr.id,
                                        text: acr.name,
                                        data: acr
                                    } as IListBoxItem;
                                })}
                                onSelect={(event, selectedAcr) => {
                                    this._onACRSelected(selectedAcr.data);
                                }}
                                filterThrottleWait={200}
                            />
                        </React.Fragment>
                        : (this.state.fetchingMoreACRs ?
                            <Spinner
                                ariaLive="assertive"
                                label={Resources.LoadingRegistries}
                                size={SpinnerSize.medium}
                                className="default-input-margin-top"
                            />
                            : <span className="default-input-margin-top">{Resources.NoRegistriesFound}</span>
                        ))
                    }
                </div>
            );
        } else {
            let isUrlEditable = this._draftServiceEndpoint.data.registrytype !== this._dockerRegistryTypeDockerHub;

            return (
                <div className="docker-registry-auth-inputs-container">
                    {this._createTextBox(InputDataType.Uri, "docker-registry",
                        this._draftServiceEndpoint.authorization.parameters.registry, "", Resources.DockerRegistry, false, false, -1, isUrlEditable, false)}
                    {this._createTextBox(InputDataType.String, "docker-id",
                        this._draftServiceEndpoint.authorization.parameters.username, "", Resources.DockerID, false, false, -1, isEditable, false)}
                    {this._createTextBox(InputDataType.String, "password",
                        this._draftServiceEndpoint.authorization.parameters.password, "", Resources.DockerPassword, true, false, -1, isEditable, false)}
                    {this._createTextBox(InputDataType.String, "e-mail",
                        this._draftServiceEndpoint.authorization.parameters.email, "", Resources.Email, false, false, -1, isEditable, true)}
                </div>
            );
        }
    }

    private _getVerificationComponent(): React.ReactNode {
        if (this.props.isVerifiableConnection && Utils_String.equals(this._draftServiceEndpoint.data.registrytype, this._dockerRegistryTypeDockerHub, true)) {
            const isVerificationDisabled: boolean = (this.state && this.state.isVerifyInProgress) || this._invalidComponentCount > 1 || (this._invalidComponentCount === 1 && !!this._draftServiceEndpoint.name && this._draftServiceEndpoint.name.trim().length > 0);
            return (
                <div className={"flex-row default-input-margin-top"}>
                    {EndpointsEditorPanelUtils.getVerifyButton(isVerificationDisabled, this._verifyEndpoint)}
                    {this.state && EndpointsEditorPanelUtils.getVerificationDetails(this.state.isVerifyInProgress, this.state.isVerificationSuccess, this.props.errorMessage)}
                </div>
            );
        } else {
            return null;
        }
    }

    private _getDetailsComponent(): React.ReactNode {
        return (
            <GenericEndpointsDetailsComponents
                disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                serviceEndpoint={this._draftServiceEndpoint}
                isUpdateForm={this.props.isUpdateForm}
                updateDraftEndpointState={this.props.updateDraftEndpointState}
                updateInvalidInputsCount={(count: number) => {
                    this._invalidComponentCount += count;
                    this._isSaveEnabled.value = this._invalidComponentCount === 0;
                }}
                updateAllowAllPipelinesFlag={this.props.updateAllowAllPipelinesFlag}
                allowAllPipelinesFlagState={this.props.allowAllPipelinesFlagState}
            />
        );
    }

    private _getFooterComponent(): React.ReactNode {
        return (
            <Observer enableSave={this._isSaveEnabled}>
                {(props: { enableSave: boolean }) => {
                    return (
                        <GenericFooterComponent
                            isUpdateForm={this.props.isUpdateForm}
                            enableSave={props.enableSave}
                            serviceEndpoint={this._draftServiceEndpoint}
                            isVerifiable={this._draftServiceEndpoint.data.registrytype === this._dockerRegistryTypeDockerHub}
                            showSaveButton={true}
                            showBackButton={!this.props.hideBackButton}
                            verifyInProgress={this.state.verifyInProgress}
                            saveInProgress={this.state.saveInProgress}
                            setupInProgress={this.state.setupInProgress}
                            onVerifyAndSaveButtonClick={this._verifyAndSaveServiceEndpoint}
                            onBackButtonClick={this.props.onBackButtonClick}
                            onSaveButtonClick={this._saveServiceEndpoint} />
                    );
                }}
            </Observer>
        );
    }

    private _verifyEndpoint = (): void => {
        this._updateVerificationStatus(true, undefined);
        this._verifyConnection().then((serviceEndpointRequestResult: ServiceEndpointRequestResult) => {
            if (serviceEndpointRequestResult.errorMessage) {
                this._updateVerificationStatus(false, false);
                this.props.updateError(true, serviceEndpointRequestResult.errorMessage, Resources.VerificationFailed);
            } else {
                this._updateVerificationStatus(false, true);
                this.props.updateError(false, "");
            }
        }).catch((error) => {
            this._updateVerificationStatus(false, false);
            const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
            this.props.updateError(true, errorMessage, Resources.VerificationFailed);
        });
    }

    private _updateVerificationStatus = (isVerifyInProgress: boolean, isVerificationSuccess: boolean): void => {
        this.setState({
            isVerifyInProgress: isVerifyInProgress,
            isVerificationSuccess: isVerificationSuccess
        });
    }

    private _verifyConnection(): Promise<ServiceEndpointRequestResult> {
        const serviceEndpointRequest: ServiceEndpointRequest = EndpointsEditorPanelUtils.getServiceEndpointRequestForTest(this.props.draftEndpoint);
        return this._endpointEditorActionCreator.verifyServiceEndpoint(serviceEndpointRequest, this.props.draftEndpoint.id);
    }

    private _onEndpointEditorStoreChange = () => {
        const storeState = this._endpointEditorStore.getState();

        if (this.state.saveInProgress && !storeState.setupInProgress && !storeState.saveInProgress && !storeState.errorMessage) {
            setTimeout(() => {
                this.props.closePanel(storeState.serviceEndpoint);
            });
        }

        if (this.state.setupInProgress && !storeState.setupInProgress && !storeState.errorMessage) {
            setTimeout(() => {
                this.props.closePanel(storeState.serviceEndpoint);
            });
        }

        this.setState({
            verifyInProgress: storeState.verifyInProgress,
            saveInProgress: storeState.saveInProgress,
            setupInProgress: storeState.setupInProgress,
            isError: storeState.isError,
            errorMessage: storeState.errorMessage
        });
    }

    private _setDefaultValuesForRegistryType(registryType: string) {
        if (registryType !== this._dockerRegistryTypeACR) {
            this._draftServiceEndpoint.authorization.scheme = ServiceEndpointConstants.EndpointAuthorizationSchemes.UsernamePassword;
            this._draftServiceEndpoint.url = registryType === this._dockerRegistryTypeDockerHub ?
                this._dockerHubDefaultUrl : this._dockerRegistryDefaultUrl;
            this._draftServiceEndpoint.data = {};
            this._draftServiceEndpoint.authorization.parameters = {
                registry: this._dockerRegistryDefaultUrl
            };

            this._initializeInputErrors();
        } else {
            if (this.props.isUpdateForm) {
                this._draftServiceEndpoint.authorization.parameters = this._acrRegistryTypeAuthParamsCache;
                this._draftServiceEndpoint.data = this._acrRegistryTypeDataParamsCache;
                this._draftServiceEndpoint.url = "https://" + this._draftServiceEndpoint.authorization.parameters.loginServer;
                this._isSaveEnabled.value = false;
            } else {
                this._draftServiceEndpoint.authorization.scheme = "";
                this._initializeInputErrors();
            }
        }

        this._draftServiceEndpoint.data.registrytype = registryType;
    }

    private _onACRAuthenticationTypeSelected(selectedAuthenticationType: string) {
        this._draftServiceEndpoint.authorization.scheme = selectedAuthenticationType;

        switch (selectedAuthenticationType) {
            case ServiceEndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal:
                this._draftServiceEndpoint.authorization.parameters = {
                    loginServer: "",
                    role: this._acrPushRoleDefinitionId,
                    scope: "",
                    servicePrincipalId: "<placeholder>",
                    tenantId: ""
                };
                this._draftServiceEndpoint.data = {
                    appObjectId: "",
                    azureSpnPermissions: "",
                    azureSpnRoleAssignmentId: "",
                    registryId: "",
                    registrytype: this._dockerRegistryTypeACR,
                    spnObjectId: "",
                    subscriptionId: "",
                    subscriptionName: ""
                };
                break;
            case ServiceEndpointConstants.EndpointAuthorizationSchemes.ManagedServiceIdentity:
                this._draftServiceEndpoint.authorization.parameters = {
                    loginServer: "",
                    tenantId: ""
                };
                this._draftServiceEndpoint.data = {
                    registryId: "",
                    registrytype: this._dockerRegistryTypeACR,
                    subscriptionId: "",
                    subscriptionName: ""
                };
                break;
            default:
                break;
        }

        this._initializeInputErrors();
        this.forceUpdate();
    }

    private _onACRSelected(selectedACR: any) {
        this.setState({selectedAcr: selectedACR});
        this._draftServiceEndpoint.url = "https://" + selectedACR.properties.loginServer;

        this._draftServiceEndpoint.authorization.parameters.loginServer = selectedACR.properties.loginServer;
        this._draftServiceEndpoint.authorization.parameters.scope = selectedACR.id;
        this._draftServiceEndpoint.authorization.parameters.tenantId = this.state.selectedSubscription.subscriptionTenantId;

        this._draftServiceEndpoint.data.registryId = selectedACR.id;
        this._draftServiceEndpoint.data.registrytype = this._dockerRegistryTypeACR;
        this._draftServiceEndpoint.data.subscriptionId = this.state.selectedSubscription.subscriptionId;
        this._draftServiceEndpoint.data.subscriptionName = this.state.selectedSubscription.displayName;

        if (!!this.state.accessTokenKey) {
            this._draftServiceEndpoint.authorization.parameters.accessTokenFetchingMethod = AccessTokenRequestType.Oauth.toString();
            this._draftServiceEndpoint.authorization.parameters.accessToken = this.state.accessTokenKey;
        }

        if (this._errorsDictionary["registry"]) {
            this._invalidComponentCount--;
            delete this._errorsDictionary["registry"];
        }

        this._isSaveEnabled.value = this._invalidComponentCount === 0;
    }

    private _getSubscriptionFromEndpoint = (): AzureSubscription => {
        if (this._draftServiceEndpoint && this._draftServiceEndpoint.data.subscriptionId) {
            return ({
                subscriptionId: this._draftServiceEndpoint.data.subscriptionId || "",
                displayName: this._draftServiceEndpoint.data.subscriptionName || "",
                subscriptionTenantId: this._draftServiceEndpoint.authorization.parameters.tenantId || "",
                subscriptionTenantName: ""
            });
        }

        return undefined;
    }

    private _getRegistryIdFromEndpoint() {
        if (this._draftServiceEndpoint && this._draftServiceEndpoint.data.registryId) {
            let registryNames = this._draftServiceEndpoint.data.registryId.split("/").reverse();
            return registryNames.length > 0 ? registryNames[0] : undefined;
        }

        return undefined;
    }

    private _onSubscriptionChanged = (subscription: Subscription | undefined): void => {
        const azureSubscription = subscription
            ? {
                displayName: subscription.displayName,
                subscriptionId: subscription.id,
                subscriptionTenantId: subscription.subscriptionAccount.subscriptionTenantId,
            } as AzureSubscription
            : undefined;

        this._onAzureSubscriptionChanged(azureSubscription);
    }

    private _onAzureSubscriptionChanged(azureSubscription: AzureSubscription) {
        if (azureSubscription && !this.props.isUpdateForm) {
            if (this._errorsDictionary["subsciption"]) {
                this._invalidComponentCount--;
                delete this._errorsDictionary["subsciption"];
            }

            this._isSaveEnabled.value = this._invalidComponentCount === 0;

            this.setState({ selectedSubscription: azureSubscription, fetchingMoreACRs: true, acrList: [] }, () => {
                this._azureEndpointService.getAzureContainerRegistries(azureSubscription, "", false, null).then((registries: any[]) => {
                    this._azureEndpointService.getAccessToken(this.state.selectedSubscription.subscriptionTenantId).then((accessToken) => {
                        this.setState({ acrList: registries, fetchingMoreACRs: false, accessTokenKey: accessToken });
                    });
                });
            });
        }
    }

    private _getACRSelection(): DropdownSelection {
        if (this.state.acrList.length && this.state.selectedAcr) {
            const selectedSubIndex = this.state.acrList.map((registry: any) => registry.id).indexOf(this.state.selectedAcr.id);
            if (selectedSubIndex >= 0) {
                this._subscriptionDropdownSelection.select(selectedSubIndex);
                return this._subscriptionDropdownSelection;
            }
        }

        return undefined;
    }

    private _createTextBox(dataType: InputDataType, inputKey: string, currentValue: string, placeholder: string, formLabel: string,
        isConfidential: boolean, isMultiline: boolean, maxLength: number, isEditable: boolean, isOptional: boolean): JSX.Element {

        let validation: InputValidation = EndpointsEditorPanelUtils.getInputValidation(dataType, !isOptional, maxLength > 0 ? maxLength : 20480);
        let uniquekey = inputKey;

        if (this._isDirtyDictionary[uniquekey] === undefined) {
            this._isDirtyDictionary[uniquekey] = false;
        }

        let error = this._errorsDictionary[uniquekey];

        return this._getTextBoxComponent(inputKey, uniquekey, currentValue, placeholder, formLabel,
            error, isConfidential, isMultiline, isEditable, this._isDirtyDictionary[uniquekey], validation, isOptional);
    }

    private _getTextBoxComponent(inputKey: string, errorKey: string, currentValue: string, placeholder: string, formLabel: string, error: string,
        isConfidential: boolean, isMultiline: boolean, isEditable: boolean, isDirtyVal: boolean | undefined, validation: InputValidation, isOptional: boolean): JSX.Element {
        let label = isOptional ? formLabel + Resources.OptionalInputSuffix : formLabel;

        return (
            <FormItem
                className="default-input-margin-top"
                label={label}
                message={isDirtyVal ? error : ""}
                error={isDirtyVal && !!error}>
                <TextField
                    key={inputKey}
                    value={currentValue}
                    onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
                        this._onTextFieldValueChanged(value, inputKey, errorKey, isConfidential, isEditable, validation);
                    }}
                    placeholder={isConfidential && this.props.isUpdateForm ? ConfidentialInputPlaceHolder : placeholder}
                    inputType={isConfidential ? "password" : "text"}
                    multiline={isMultiline}
                    disabled={!isEditable}
                    maxLength={(validation && validation.maxLength && validation.maxLength > 0)
                        ? validation.maxLength : this._DEFAULT_TEXTFIELD_MAXLENGTH}
                />
            </FormItem>
        );
    }

    private _onTextFieldValueChanged(value: string, inputKey: string, errorKey: string, isConfidential: boolean, isEditable: boolean, validation: InputValidation) {
        switch (inputKey) {
            case "e-mail":
                this._draftServiceEndpoint.authorization.parameters.email = value;
                break;
            case "password":
                this._draftServiceEndpoint.authorization.parameters.password = value;
                break;
            case "docker-registry":
                this._draftServiceEndpoint.authorization.parameters.registry = value;
                this._draftServiceEndpoint.url = value;
                break;
            case "docker-id":
                this._draftServiceEndpoint.authorization.parameters.username = value;
                break;
            case "subscription-id":
                this._draftServiceEndpoint.data.subscriptionId = value;
                break;
            case "subscription-name":
                this._draftServiceEndpoint.data.subscriptionName = value;
                break;
            case "tenant-id":
                this._draftServiceEndpoint.authorization.parameters.tenantId = value;
                break;
            case "acr-login-server":
                this._draftServiceEndpoint.authorization.parameters.loginServer = value;
                this._draftServiceEndpoint.url = "https://" + value;
                break;
            default:
                return;
        }

        this._updateInputError(value, validation, inputKey, isConfidential);
        this._isDirtyDictionary[inputKey] = true;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
    }

    private _verifyAndSaveServiceEndpoint = () => {
        this._endpointEditorActionCreator.verifyAndSaveServiceEndpoint(this._draftServiceEndpoint, TestConnectionDataSource, this.props.allowAllPipelinesFlagState, this.props.isUpdateForm);
    }

    private _saveServiceEndpoint = () => {

        if (this.props.isUpdateForm) {
            this._endpointEditorActionCreator.updateServiceEndpoint(this._draftServiceEndpoint).then((result: ServiceEndpoint) => {
                this._endpointEditorActionCreator.saveResourceAuthorizationState(this._draftServiceEndpoint.id, this.props.allowAllPipelinesFlagState);
                this.props.closePanel(result);
            }).catch((error) => {
                const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
                this.props.updateError(true, errorMessage, Resources.VerificationFailed);
            });
        } else {
            this._endpointEditorActionCreator.customSaveServiceEndpoint(this._draftServiceEndpoint, this.props.allowAllPipelinesFlagState, this.props.isUpdateForm);
        }
    }

    private _initializeInputErrors() {
        // Clear existing error count
        this._invalidComponentCount -= Object.keys(this._errorsDictionary).length;
        this._errorsDictionary = {};

        if (this._draftServiceEndpoint.data.registrytype !== this._dockerRegistryTypeACR) {
            let validation: InputValidation = EndpointsEditorPanelUtils.getInputValidation(InputDataType.Uri, true, this._DEFAULT_TEXTFIELD_MAXLENGTH);
            this._updateInputError(this._draftServiceEndpoint.authorization.parameters.registry, validation, "docker-registry", false);
            validation = EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, true, this._DEFAULT_TEXTFIELD_MAXLENGTH);
            this._updateInputError(this._draftServiceEndpoint.authorization.parameters.username, validation, "docker-id", false);
            validation = EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, false, this._DEFAULT_TEXTFIELD_MAXLENGTH);
            this._updateInputError(this._draftServiceEndpoint.authorization.parameters.email, validation, "e-mail", false);
            validation = EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, true, this._DEFAULT_TEXTFIELD_MAXLENGTH);
            this._updateInputError(this._draftServiceEndpoint.authorization.parameters.password, validation, "password", true);

        } else {
            if (!this.props.isUpdateForm) {
                if (this._draftServiceEndpoint.authorization.scheme === ServiceEndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal) {
                    if (!this.state.selectedSubscription) {
                        this._errorsDictionary["subsciption"] = "invalid";
                        this._invalidComponentCount++;
                    }

                    if (!this.state.selectedAcr) {
                        this._errorsDictionary["registry"] = "invalid";
                        this._invalidComponentCount++;
                    }
                }
            }
        }

        this._isSaveEnabled.value = this._invalidComponentCount === 0;
    }

    private _updateInputError(value: any, inputValidation: InputValidation, inputId: string, isConfidential: boolean) {
        if (this.props.isUpdateForm && isConfidential) {
            return;
        }

        if (!EndpointsEditorPanelUtils.isInputValid(value, inputValidation)) {
            if (!this._errorsDictionary[inputId]) {
                this._invalidComponentCount++;
            }

            this._errorsDictionary[inputId] = EndpointsEditorPanelUtils.getInvalidInputErrorMessage(inputValidation);
        } else {
            if (this._errorsDictionary[inputId]) {
                this._invalidComponentCount--;
            }

            delete this._errorsDictionary[inputId];
        }

        this._isSaveEnabled.value = this._invalidComponentCount === 0;
    }

    private _showOAuthScheme(): boolean {
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        return vssPageData.isHosted;
    }

    private readonly _acrPushRoleDefinitionId = "8311e382-0749-4cb8-b61a-304f252e45ec";
    private readonly _DEFAULT_TEXTFIELD_MAXLENGTH = 20480;
    private readonly _dockerRegistryTypeACR = "ACR";
    private readonly _dockerRegistryTypeDockerHub = "DockerHub";
    private readonly _dockerRegistryTypeOthers = "Others";
    private readonly _dockerRegistryDefaultUrl = "https://index.docker.io/v1/";
    private readonly _dockerHubDefaultUrl = "https://hub.docker.com/";

    private _errorsDictionary: { [key: string]: string } = {};
    private _isDirtyDictionary: { [key: string]: boolean } = {};
    private _isSaveEnabled: ObservableValue<boolean>;
    private _invalidComponentCount: number;
    private _draftServiceEndpoint: ServiceEndpoint;
    private _azureEndpointService: IAzureResourcesService;
    private _endpointEditorActionCreator: EndpointsEditorActionsCreator;
    private _endpointEditorStore: EndpointsEditorStore;
    private _acrRegistryTypeDataParamsCache: { [key: string]: any } = {};
    private _acrRegistryTypeAuthParamsCache: { [key: string]: any } = {};
    private _subscriptionDropdownSelection = new DropdownSelection();
    private _authenticationTypeOptions: IListBoxItem<string>[] = [];
}

VssComponent.register("endpoint-editor-component-docker-registry", DockerRegistryEndpointEditorComponent);
